import { CombatCalculator } from "@/core/battle/CombatCalculator";
import { CombatExecutor } from "@/core/battle/CombatExecutor";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { MovementExecutor } from "@/core/battle/MovementExecutor";
import { ControlState, FactionType, PathInfo, PathSegment, TerrainType } from "@/core/types/battle.types";
import { coordsToId } from "@/core/utils/hex.utils";
import { useBattleStore } from "@/store/useBattleStore";


export class ExecutorManager {

    constructor(
        private movementExecutor: MovementExecutor,
        private combatExecutor: CombatExecutor,
        private stateMachine: ControlStateMachine,
    ) {}

    async move(unitId: string, pathInfo: PathInfo): Promise<void> {
        const units = useBattleStore.getState().units;
        const segments = this.splitPathByZoc(unitId, pathInfo, units);
        // 2. Set state to PlayerMoving
        const unit = units.get(unitId);
        if (!unit) return;
        const isPlayer = unit.faction === FactionType.Player;
        if (isPlayer && this.stateMachine.is(ControlState.Idle)) {
            this.stateMachine.setState(ControlState.PlayerMoving);
        }
        try {
            for (const segment of segments) {
                const isOpportunityStrike = segment.pathInfo.path.length !== pathInfo.path.length;
                await this.movementExecutor.execute(unitId, segment.pathInfo, isOpportunityStrike);

                for (const attacker of segment.attackers) {
                    const skillId = units.get(attacker.attackerId)?.skills[0]?.id
                    await this.attack(attacker.attackerId, unitId, skillId!, {forcedHit: attacker.hit, opportunityAttack: true});
                    // useBattleStore.getState().updateUnit(attacker.attackerId, { opportunityAttackUsed: true, currentStamina: attacker.stamina });
                    if (attacker.hit) break;
                }

                if (segment.attackers.some(a => a.hit)) {
                //     // useBattleStore.getState().updateUnit(unitId, { currentStamina: 0 });
                    break;
                }
            }
        } catch (error) {
            // Якщо в анімації сталась помилка, ми ловимо її тут, щоб не впав весь додаток
            console.error("[ExecutorManager] Move sequence failed:", error);
        } finally {
            // 2. ГАРАНТОВАНЕ розблокування (виконається навіть при помилці)
            if (this.stateMachine.is(ControlState.PlayerMoving)) {
                this.stateMachine.setState(ControlState.Idle);
            }
        }
    }

    async attack(attackerId: string, targetId: string, skillId: string, options?: { forcedHit?: boolean, opportunityAttack: boolean }): Promise<void> {
        const { units, targetHexes } = useBattleStore.getState();
        const unit = units.get(attackerId);
        const target = units.get(targetId);
        if (!unit || !target) return;

        const isOpportunityStrike: boolean = options?.opportunityAttack === true;
        const isPlayer = unit.faction === FactionType.Player;
        const targetHexId = coordsToId(target);
        if (isPlayer&& !isOpportunityStrike && !targetHexes.includes(targetHexId)) {
            console.warn('[CombatExecutor] Unit not in range');
            return;
        }
        if (isPlayer && this.stateMachine.is(ControlState.SkillTargeting)) {
            this.stateMachine.setState(ControlState.PlayerAttacking);
        }
        try {
            await this.combatExecutor.execute(attackerId, targetId, skillId, options);
        } catch (error) {
            // Якщо в анімації сталась помилка, ми ловимо її тут, щоб не впав весь додаток
            console.error("[ExecutorManager] Move sequence failed:", error);
        } finally {
            // 2. ГАРАНТОВАНЕ розблокування (виконається навіть при помилці)
            if (!isOpportunityStrike && this.stateMachine.is(ControlState.PlayerAttacking)) {
                this.stateMachine.setState(ControlState.Idle);
            }
        }
        
    }

    private splitPathByZoc(
        unitId: string,
        pathInfo: PathInfo,
        units: ReturnType<typeof useBattleStore.getState>['units']
    ): PathSegment[] {
        const segments: PathSegment[] = [];
        const usedAttackers = new Set<string>();
        let segmentStart = 0;

        const unit = units.get(unitId)!;
        // fullPath[i] — це гекс, на якому ми стоїмо ПЕРЕД кроком i
        const fullPath = [coordsToId(unit), ...pathInfo.path];

        for (let i = 0; i < pathInfo.path.length; i++) {
            const leavingHexId = fullPath[i]!;
            const attackers = CombatCalculator.checkOpportunityAttackersOnHex(unitId, leavingHexId, units, usedAttackers);

            if (attackers.length > 0) {
                attackers.forEach(a => usedAttackers.add(a.attackerId));
                // РІШЕННЯ: Ріжемо шлях ДО поточного гекса (i - 1)
                // Якщо i=0, slice(0, 0) поверне порожній масив — це правильно (атака прямо на старті)
                segments.push({
                    pathInfo: this.slicePathInfo(unitId, pathInfo, segmentStart, i - 1, attackers.some(a => a.hit), segments.length),
                    attackers,
                });

                if (attackers.some(a => a.hit)) return segments;

                // Наступний сегмент почнеться з цієї ж точки, з якої ми намагалися вийти
                segmentStart = i;
            }
        }

        // Додаємо фінальний відрізок шляху, якщо він залишився
        if (segmentStart < pathInfo.path.length) {
            segments.push({
                pathInfo: this.slicePathInfo(unitId, pathInfo, segmentStart, pathInfo.path.length - 1, false, segments.length),
                attackers: [],
            });
        }

        return segments;
    }

    private slicePathInfo(
        unitId: string, 
        pathInfo: PathInfo, 
        from: number, 
        to: number, 
        isInterrupted: boolean,
        segmentIndex: number
    ): PathInfo {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId)!;

        // 1. Обробка порожнього шляху (удар на першому ж кроці)
        if (from > to) {
            const startHexId = coordsToId(unit);
            const hex = state.battleMap.get(startHexId);

            let finalCost = 0;
            if (isInterrupted) {
                finalCost = unit.currentStamina; // Знімаємо все, що є
            } else if (segmentIndex === 0) {
                finalCost = pathInfo.cost; // Знімаємо ціну всього запланованого шляху
            }

            return {
                path: [],
                lastStep: { id: startHexId, terrain: hex?.terrain || TerrainType.Plains },
                cost: finalCost
            };
        }

        // 2. Звичайний сегмент або перехоплення далі по шляху
        const slicedPath = pathInfo.path.slice(from, to + 1);
        const lastHexId = slicedPath[slicedPath.length - 1]!;
        const lastHex = state.battleMap.get(lastHexId);

        let finalCost = 0;
        if (isInterrupted) {
            finalCost = unit.currentStamina; // Знімаємо все, що є
        } else if (segmentIndex === 0) {
            finalCost = pathInfo.cost; // Знімаємо ціну всього запланованого шляху
        }

        return {
            path: slicedPath,
            lastStep: { 
                id: lastHexId, 
                terrain: lastHex?.terrain || TerrainType.Plains 
            },
            cost: finalCost
        };
    }
}